import type { ErrorEvent, EventHint, StackFrame } from '@sentry/types';
import {
  commonClientIgnoreErrors,
  ignoredErrorCodes,
  ignoredErrorMessages,
  ignoredErrorNames,
  ignoredStackTraces,
} from './client-ignore-lists';

let sentryErrors = 0;
const MAX_CLIENT_ERRORS = 50;

export function resetSentryErrorCounter(): void {
  sentryErrors = 0;
}

const allowStacktraces = [
  new RegExp('./src.*.[jt]sx?'),
  new RegExp('packages.*.[jt]sx?'),
  new RegExp('design.*.[jt]sx?'),
  new RegExp('question-modules.*.[jt]sx?'),
];

declare global {
  interface Window {
    next?: any;
  }
}

/**
 * Interface that extends normal errors with extra attributes found in third party errors
 */
interface ExtendedError extends Error {
  code?: number;
  statusCode?: number;
}

export function beforeSend(
  event: ErrorEvent,
  hint: EventHint,
  originalException: ExtendedError,
): ErrorEvent | null {
  if (
    event.environment === 'dev' &&
    process.env.LINKUP_LOG_LEVEL &&
    process.env.LINKUP_LOG_LEVEL !== 'silent'
  ) {
    // eslint-disable-next-line no-console
    console.warn('[Mentimeter] Sentry exception received:', event, hint);
  }

  /*
   * Ignore errors when reequest url is not set and transaction is not set or is not a string
   */
  if (
    !event.request?.url ||
    !event.transaction ||
    typeof event.transaction !== 'string'
  ) {
    return null;
  }

  /*
   * Ignore errors
   */
  // Ignore repeated errors from the same client
  if (++sentryErrors > MAX_CLIENT_ERRORS) {
    return null;
  }

  if (isUnsupportedOS()) {
    return null;
  }

  if (isUnsupportedBrowser()) {
    return null;
  }

  // Ignore error from ignoreLists

  const shouldIgnoreFromLists =
    getShouldIgnoreFromIgnoreLists(originalException) ||
    getShouldIgnoreFromIgnoreLists(originalException.cause as ExtendedError) ||
    getShouldIgnoreFromIgnoreLists(
      (originalException.cause as ExtendedError | undefined)
        ?.cause as ExtendedError,
    );

  if (shouldIgnoreFromLists) {
    return null;
  }

  /**
   * Datadog: we're ignoring errors from "common client errors" that would
   * have already been ignored by Sentry (see `extraClientConfig`).
   */
  const shouldIgnoreFromCommonClientErrors =
    getShouldIgnoreFromCommonClientErrors(originalException) ||
    getShouldIgnoreFromCommonClientErrors(
      originalException.cause as ExtendedError,
    );

  if (shouldIgnoreFromCommonClientErrors) {
    return null;
  }

  // Ignore errors from local drive
  if (event.request?.url?.startsWith('file://')) {
    return null;
  }

  const isAblyError = isAblyIssue(originalException);

  // Ignore errors without stacktraces
  if (
    event?.exception?.values &&
    event.exception.values[0] &&
    !event.exception.values[0].stacktrace &&
    !isAblyError
  ) {
    return null;
  }

  // Ignore errors with bad stacktraces
  if (
    event?.exception?.values &&
    event.exception.values[0] &&
    isBadStacktrace(event.exception.values[0].stacktrace?.frames)
  ) {
    return null;
  }

  const isCSPError = isCSPIssue(event, hint);

  if (isCSPError) {
    // Only works for Datadog: sample CSP to 1%
    if (Math.random() > 0.01) {
      return null;
    }
  }

  /*
   * Modify errors
   */
  if (
    isAblyError &&
    event.exception &&
    event.exception.values &&
    event.exception.values[0]
  ) {
    event.exception.values[0].value = `Ably Error: code ${originalException.code}, statusCode ${originalException.statusCode}, ${originalException.message}`;
  }

  /*
   * Group errors
   */

  return groupExceptions(event, originalException);
}

const getShouldIgnoreFromCommonClientErrors = (
  ex: ExtendedError | string | undefined,
): boolean => {
  if (typeof ex !== 'object') {
    return false;
  }

  const getMatcher = (str: string | undefined) => (rx: RegExp | string) => {
    if (!str) {
      return false;
    }

    if (typeof rx === 'string') {
      return str.includes(rx);
    }

    return rx.test(str);
  };

  return !!(
    commonClientIgnoreErrors.some(getMatcher(ex.name)) ||
    commonClientIgnoreErrors.some(getMatcher(ex.message))
  );
};

const getShouldIgnoreFromIgnoreLists = (
  ex: ExtendedError | string | undefined,
): boolean => {
  if (typeof ex !== 'object') {
    return false;
  }

  return !!(
    (ex.code && ignoredErrorCodes.includes(ex.code)) ||
    (ex.message &&
      ignoredErrorMessages.some((rx: RegExp) => rx.test(ex.message))) ||
    (ex.name && ignoredErrorNames.some((rx: RegExp) => rx.test(ex.name)))
  );
};

function groupExceptions(event: ErrorEvent, exception: ExtendedError) {
  const fingerprint: string[] = [];
  fingerprint.push(window.next?.router?.state?.pathname ?? 'no-next-path');
  for (const grouping of issueGrouping) {
    if (grouping.regex.test(exception.message)) {
      fingerprint.push(grouping.fingerprint);
    }
  }
  if (isAblyIssue(exception)) {
    fingerprint.push(`ably-error-${exception.code}`);
  }

  // Only write fingerprint when more than next path is added
  if (fingerprint.length > 1) {
    event.fingerprint = fingerprint;
  }
  return event;
}

const issueGrouping: { regex: RegExp; fingerprint: string }[] = [
  {
    regex: /Invariant\: attempted to hard navigate to the same URL.*/,
    fingerprint: 'hard-navigate-next',
  },
];

function isAblyIssue(exception: ExtendedError): boolean {
  const ablyErrorCodes = [
    10000, 40000, 40001, 40005, 40006, 40006, 40009, 40010, 40012, 40013, 40030,
    40032, 40100, 40101, 40102, 40103, 40104, 40105, 40111, 40112, 40114, 40115,
    40131, 40142, 40143, 40144, 40160, 40161, 40171, 40300, 40330, 40331, 40332,
    40400, 42910, 42911, 50000, 50000, 50001, 50003, 61002, 70002, 80000, 80002,
    80003, 80008, 80016, 80017, 80019, 80021, 80022, 90001, 90007, 90010, 90021,
    91000, 91001, 91005,
  ];
  if (
    exception.code &&
    exception.statusCode &&
    ablyErrorCodes.includes(exception.code)
  ) {
    return true;
  } else {
    return false;
  }
}

function isBadStacktrace(stackFrames: StackFrame[] | undefined): boolean {
  if (!stackFrames) {
    return false;
  }

  //ignore if value is in filename or abspath
  const ignoreIssue = stackFrames.some((frame) =>
    ignoredStackTraces.some(
      (rx) =>
        frame.filename &&
        frame.abs_path &&
        (rx.test(frame.filename) || rx.test(frame.abs_path)),
    ),
  );

  //allow if value exists in filename or abspath
  const allowIssue = stackFrames.some((frame) =>
    allowStacktraces.some(
      (rx) =>
        frame.filename &&
        frame.abs_path &&
        (rx.test(frame.filename) || rx.test(frame.abs_path)),
    ),
  );

  return ignoreIssue && !allowIssue;
}

/**
 * List of compatible browsers and their minimum versions based on our "BrowsersList" file.
 * @see http://github.com/mentimeter/mm-js/blob/main/applications/editor/.browserslistrc
 * @see https://www.mentimeter.com/requirements
 */
const MIN_BROWSER_VERSIONS = {
  chrome: 123,
  firefox: 129,
  safari: 16.4,
  edge: 130,
  android: 13,
  chromeAndroid: 123,
  firefoxAndroid: 127,
  ios: 15.8,
} as const;

const MIN_OS_VERSIONS = {
  android: 13,
  ios: 15.8,
} as const;

function getBrowserVersion(): {
  name: keyof typeof MIN_BROWSER_VERSIONS;
  version: number;
} | null {
  if (typeof navigator === 'undefined' || !navigator?.userAgent) {
    /**
     * Navigator might not be available in server environments.
     */
    return null;
  }

  const ua = navigator.userAgent;

  /**
   * Detect Chrome (excluding Edge Chromium)
   */
  const chromeMatch = ua.match(/Chrom(?:e|ium)\/(\d+)/);
  if (chromeMatch && !ua.includes('Edg/')) {
    return {
      name: 'chrome',
      version: parseInt(chromeMatch[1] || '0', 10),
    };
  }

  /**
   * Detect Firefox
   */
  const firefoxMatch = ua.match(/Firefox\/(\d+)/);
  if (firefoxMatch) {
    return {
      name: 'firefox',
      version: parseInt(firefoxMatch[1] || '0', 10),
    };
  }

  /**
   * Detect iOS Version (For Safari on iOS)
   */
  const iosMatch = ua.match(/Version\/(\d+(?:\.\d+){0,2})/);
  if (iosMatch && ua.includes('Safari') && ua.includes('Mobile')) {
    return {
      name: 'ios',
      version: parseFloat(iosMatch[1] || '0'),
    };
  }

  /**
   * Detect Safari (excluding Chrome on iOS)
   */
  const safariMatch = ua.match(/Version\/(\d+(?:\.\d+)?).*Safari/);
  if (safariMatch && ua.includes('Safari') && !ua.includes('Chrome')) {
    return {
      name: 'safari',
      version: parseFloat(safariMatch[1] || '0'),
    };
  }

  /**
   * Detect Edge Chromium
   */
  const edgeMatch = ua.match(/Edg\/(\d+)/);
  if (edgeMatch) {
    return {
      name: 'edge',
      version: parseInt(edgeMatch[1] || '0', 10),
    };
  }

  /**
   * Detect Chrome on Android
   */
  const chromeAndroidMatch = ua.match(/Chrome\/(\d+).*Mobile/);
  if (chromeAndroidMatch) {
    return {
      name: 'chromeAndroid',
      version: parseInt(chromeAndroidMatch[1] || '0', 10),
    };
  }

  /**
   * Detect Firefox on Android
   */
  const firefoxAndroidMatch = ua.match(/Firefox\/(\d+).*Android/);
  if (firefoxAndroidMatch) {
    return {
      name: 'firefoxAndroid',
      version: parseInt(firefoxAndroidMatch[1] || '0', 10),
    };
  }

  /**
   * Detect Android WebView (Generic Android Browser)
   */
  const androidMatch = ua.match(/Android (\d+)/);
  if (androidMatch) {
    return {
      name: 'android',
      version: parseInt(androidMatch[1] || '0', 10),
    };
  }

  return null;
}

function getOS(): {
  name: keyof typeof MIN_BROWSER_VERSIONS;
  version: number;
} | null {
  if (typeof navigator === 'undefined' || !navigator?.userAgent) {
    /**
     * Navigator might not be available in server environments.
     */
    return null;
  }

  const ua = navigator.userAgent;

  // detect android operating system
  const androidMatch = ua.match(/Android (\d+)/);
  if (androidMatch) {
    return {
      name: 'android',
      version: parseInt(androidMatch[1] || '0', 10),
    };
  }

  // detect ios operating system
  const iosMatch = ua.match(/Version\/(\d+(?:\.\d+){0,2})/);
  if (iosMatch && ua.includes('Safari') && ua.includes('Mobile')) {
    return {
      name: 'ios',
      version: parseFloat(iosMatch[1] || '0'),
    };
  }

  return null;
}

function isUnsupportedBrowser(): boolean {
  const browser = getBrowserVersion();
  if (!browser) {
    /**
     * Keep non-listed browsers.
     */
    return false;
  }

  const minVersion = MIN_BROWSER_VERSIONS[browser.name];
  return browser.version < minVersion;
}

function isUnsupportedOS(): boolean {
  const os = getOS();
  if (!os) {
    return false;
  }
  const minVersion = MIN_OS_VERSIONS[os.name as keyof typeof MIN_OS_VERSIONS];
  return os.version < minVersion;
}

export function isCSPIssue(event: ErrorEvent, hint: EventHint): boolean {
  return (
    // @ts-expect-error: we send this name from Datadog ourselves
    hint?.originalException?.name?.toLowerCase().includes('csp') ||
    event?.exception?.values?.some(
      (val) =>
        val.type?.toLowerCase().includes('csp') ||
        val.value?.toLowerCase().includes('csp_violation') ||
        val.value?.toLowerCase().includes('content security policy'),
    ) ||
    event.type
      // @ts-expect-error: in the Sentry interface event type CSP exists (however, it might not be caught by beforeSend)
      ?.toLowerCase()
      .includes('csp')
  );
}
